import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ShieldCheck, Trash2, FileText } from "lucide-react";

function BookingLegalText({ field, titel }) {
  const [text, setText] = useState("");
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/booking/rechtstexte`)
      .then((r) => r.json()).then((d) => setText(d[field] || "")).catch(() => {});
  }, [field]);
  if (!text) return null;
  return (
    <div className="mt-10 pt-8 border-t border-neutral-200" data-testid={`booking-legal-${field}`}>
      <h2>{titel}</h2>
      {text.split("\n").map((line, i) => {
        const t = line.trim();
        if (!t) return null;
        if (t.startsWith("#")) return <h3 key={i} className="font-head font-bold text-neutral-900 mt-5">{t.replace(/^#+\s*/, "")}</h3>;
        return <p key={i} className="mt-2">{t}</p>;
      })}
    </div>
  );
}

function LegalShell({ icon: Icon, kicker, title, children }) {
  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center gap-3">
          <img src="/innovationshafen-favicon-orange.png" alt="Innovationshafen" className="h-8 w-8 rounded-lg" />
          <div className="leading-tight">
            <p className="font-head font-bold text-[15px] text-neutral-900">SM-Regio</p>
            <p className="text-[11px] text-neutral-400">Eine Initiative des Innovationshafens</p>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-10">
        <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400 flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" /> {kicker}
        </p>
        <h1 className="font-head text-3xl font-bold text-neutral-900 tracking-tight mt-1 mb-8">{title}</h1>
        <div className="space-y-6 text-[15px] leading-relaxed text-neutral-700 [&_h2]:font-head [&_h2]:text-lg [&_h2]:font-bold [&_h2]:text-neutral-900 [&_h2]:mt-8 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
        <footer className="mt-12 pt-6 border-t border-neutral-200 text-sm text-neutral-400 flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/datenschutz" className="hover:text-neutral-700">Datenschutz</Link>
          <Link to="/datenloeschung" className="hover:text-neutral-700">Datenlöschung</Link>
          <Link to="/nutzungsbedingungen" className="hover:text-neutral-700">Nutzungsbedingungen</Link>
          <span>© {new Date().getFullYear()} Innovationshafen</span>
        </footer>
      </main>
    </div>
  );
}

export function Datenschutz() {
  return (
    <LegalShell icon={ShieldCheck} kicker="Rechtliches" title="Datenschutzerklärung">
      <div data-testid="legal-datenschutz">
        <p>
          Diese Datenschutzerklärung informiert über die Verarbeitung personenbezogener Daten in der
          Redaktions- und Social-Media-Anwendung <strong>„SM-Regio“ (KI-System SM-Regio)</strong>.
        </p>

        <h2>1. Verantwortlicher</h2>
        <p>
          Innovationshafen<br />
          E-Mail: <a href="mailto:hallo@innovationshafen.net" className="text-blue-600 hover:underline">hallo@innovationshafen.net</a>
        </p>

        <h2>2. Zweck der Anwendung</h2>
        <p>
          SM-Regio ist ein internes Redaktionswerkzeug zur Planung, Erstellung und Veröffentlichung von
          Social-Media-Inhalten für lokale Kanäle (u.a. Instagram). Die Anwendung wird ausschließlich vom
          Redaktionsteam und autorisierten Personen genutzt.
        </p>

        <h2>3. Verarbeitete Daten</h2>
        <ul>
          <li><strong>Nutzerkonten:</strong> Name, E-Mail-Adresse, verschlüsseltes Passwort, Rolle im Team.</li>
          <li><strong>Inhaltsdaten:</strong> Redaktionsinhalte, Bilder, Videos, Kommentare und Chat-Nachrichten des Teams.</li>
          <li>
            <strong>Instagram-/Meta-Daten:</strong> Bei Verbindung eines Instagram-Business-Kontos verarbeiten wir über die
            Meta-Graph-API Zugriffstokens, Profilinformationen (Nutzername, Follower-Zahl), Beitrags-Statistiken
            (Reichweite, Likes, Kommentare) sowie – bei aktivierter DM-Automation – öffentliche Kommentare zu eigenen
            Beiträgen (Kommentartext, Nutzername des Kommentierenden), um automatisierte Antworten zu versenden.
          </li>
          <li><strong>Technische Daten:</strong> Login-Zeitpunkte und technisch notwendige Cookies (Sitzungs-Tokens).</li>
        </ul>

        <h2>4. Rechtsgrundlagen</h2>
        <p>
          Die Verarbeitung erfolgt auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO (Bereitstellung der Anwendung),
          Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an effizienter Redaktionsarbeit und Kommunikation)
          sowie – für verbundene Social-Media-Konten – Ihrer Einwilligung im Rahmen der Meta-Anmeldung
          (Art. 6 Abs. 1 lit. a DSGVO).
        </p>

        <h2>5. Meta-Plattformen (Instagram)</h2>
        <p>
          Für die Anbindung an Instagram nutzen wir die offiziellen Schnittstellen der Meta Platforms Ireland Ltd.
          Dabei gelten ergänzend die Datenrichtlinien von Meta. Zugriffstokens werden verschlüsselt gespeichert und
          ausschließlich für die von Ihnen autorisierten Funktionen (Veröffentlichen, Statistiken, Antworten auf
          Kommentare) verwendet. Eine Weitergabe an Dritte findet nicht statt.
        </p>

        <h2>6. Speicherdauer</h2>
        <p>
          Daten werden gespeichert, solange das jeweilige Nutzerkonto bzw. die Kontoverbindung besteht.
          Bei Trennung der Instagram-Verbindung werden die zugehörigen Zugriffstokens gelöscht.
          Details zur Löschung finden Sie unter <Link to="/datenloeschung" className="text-blue-600 hover:underline">Datenlöschung</Link>.
        </p>

        <h2>7. Ihre Rechte</h2>
        <p>
          Sie haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung der Verarbeitung,
          Datenübertragbarkeit sowie Widerspruch (Art. 15–21 DSGVO). Wenden Sie sich dazu an
          {" "}<a href="mailto:hallo@innovationshafen.net" className="text-blue-600 hover:underline">hallo@innovationshafen.net</a>.
          Außerdem besteht ein Beschwerderecht bei einer Datenschutz-Aufsichtsbehörde.
        </p>

        <h2>8. Stand</h2>
        <p>Diese Datenschutzerklärung wurde zuletzt im Juni 2026 aktualisiert.</p>
        <BookingLegalText field="datenschutz_text" titel="Ergänzung: Buchungsmodul „Digitaler Content für Bruchköbel“" />
      </div>
    </LegalShell>
  );
}

export function Datenloeschung() {
  return (
    <LegalShell icon={Trash2} kicker="Rechtliches" title="Anleitung zur Datenlöschung">
      <div data-testid="legal-datenloeschung">
        <p>
          Hier erfahren Sie, wie Sie die Löschung Ihrer im Zusammenhang mit <strong>„KI-System SM-Regio“</strong>
          {" "}gespeicherten Daten veranlassen können.
        </p>

        <h2>Option 1: Instagram-/Meta-Verbindung selbst entfernen</h2>
        <ul>
          <li>Öffnen Sie <strong>instagram.com → Einstellungen → Website-Berechtigungen → Apps und Websites</strong>.</li>
          <li>Entfernen Sie dort die App <strong>„KI-System SM-Regio“</strong>.</li>
          <li>Damit wird der Zugriff der Anwendung auf Ihr Instagram-Konto sofort widerrufen. Gespeicherte Zugriffstokens werden ungültig und bei der nächsten Bereinigung gelöscht.</li>
        </ul>

        <h2>Option 2: Löschung per E-Mail beantragen</h2>
        <p>
          Senden Sie eine formlose E-Mail an
          {" "}<a href="mailto:hallo@innovationshafen.net?subject=Datenl%C3%B6schung%20SM-Regio" className="text-blue-600 hover:underline">hallo@innovationshafen.net</a>
          {" "}mit dem Betreff <strong>„Datenlöschung SM-Regio“</strong> und nennen Sie Ihren Instagram-Nutzernamen
          bzw. Ihre im System hinterlegte E-Mail-Adresse.
        </p>
        <p>Wir löschen daraufhin innerhalb von <strong>14 Tagen</strong>:</p>
        <ul>
          <li>alle gespeicherten Zugriffstokens und Verbindungsdaten Ihres Kontos,</li>
          <li>Ihr Nutzerkonto samt Profildaten (sofern vorhanden),</li>
          <li>personenbezogene Daten aus Kommentar-/Automationsprotokollen.</li>
        </ul>
        <p>Sie erhalten eine Bestätigung, sobald die Löschung abgeschlossen ist.</p>

        <h2>Hinweis für Kommentierende</h2>
        <p>
          Wenn Sie einen Beitrag unserer Kanäle kommentiert haben und eine automatisierte Antwort erhalten haben,
          können Sie ebenfalls über Option 2 die Löschung der zugehörigen Protokolldaten (Kommentartext, Nutzername)
          verlangen.
        </p>
      </div>
    </LegalShell>
  );
}

export function Nutzungsbedingungen() {
  return (
    <LegalShell icon={FileText} kicker="Rechtliches" title="Nutzungsbedingungen">
      <div data-testid="legal-nutzungsbedingungen">
        <h2>1. Geltungsbereich</h2>
        <p>
          Diese Nutzungsbedingungen gelten für die Nutzung der Anwendung <strong>„SM-Regio“ (KI-System SM-Regio)</strong>,
          betrieben vom Innovationshafen. Die Anwendung ist ein internes Redaktionswerkzeug für autorisierte
          Team-Mitglieder und Partner.
        </p>

        <h2>2. Zugang</h2>
        <p>
          Der Zugang erfolgt über persönliche Zugangsdaten, die nicht an Dritte weitergegeben werden dürfen.
          Der Betreiber kann Zugänge jederzeit sperren, wenn ein Missbrauch vorliegt.
        </p>

        <h2>3. Inhalte und Verantwortung</h2>
        <p>
          Nutzer sind für die von ihnen erstellten und veröffentlichten Inhalte verantwortlich. Es dürfen keine
          rechtswidrigen Inhalte erstellt, hochgeladen oder über verbundene Social-Media-Konten veröffentlicht werden.
          Bei der Nutzung verbundener Plattformen (z.B. Instagram) gelten zusätzlich deren Nutzungsbedingungen.
        </p>

        <h2>4. Verfügbarkeit</h2>
        <p>
          Der Betreiber bemüht sich um eine hohe Verfügbarkeit der Anwendung, übernimmt jedoch keine Garantie für
          einen unterbrechungsfreien Betrieb.
        </p>

        <h2>5. Kontakt</h2>
        <p>
          Fragen zu diesen Bedingungen richten Sie bitte an
          {" "}<a href="mailto:hallo@innovationshafen.net" className="text-blue-600 hover:underline">hallo@innovationshafen.net</a>.
        </p>

        <h2>6. Stand</h2>
        <p>Juni 2026</p>
        <BookingLegalText field="agb_text" titel="AGB & Vertragshinweise: „Digitaler Content für Bruchköbel“" />
      </div>
    </LegalShell>
  );
}
